import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useRoles } from "@/hooks/useRole";

export type CoachFeature =
  | "coach_chat"
  | "coach_assess"
  | "project_coach"
  | "project_recommendations";

// Mirror of server-side CAPS in supabase/functions/_shared/coachQuota.ts.
// Display-only; server is the source of truth.
const CAPS: Record<string, Record<CoachFeature, number>> = {
  partner_admin: { coach_chat: 40, coach_assess: 3, project_coach: 40, project_recommendations: 10 },
  member:        { coach_chat: 15, coach_assess: 2, project_coach: 15, project_recommendations: 5 },
  guest:         { coach_chat: 5,  coach_assess: 1, project_coach: 5,  project_recommendations: 2 },
};

/**
 * Returns the current user's Coach usage for a given feature today (UTC),
 * plus their cap. Returns `cap = null` for super_admin (unlimited).
 */
export function useCoachQuota(feature: CoachFeature) {
  const { user } = useAuth();
  const { isSuperAdmin, isPartnerAdmin, isMember, isGuest } = useRoles();

  const tier: keyof typeof CAPS | "super_admin" | "none" = isSuperAdmin
    ? "super_admin"
    : isPartnerAdmin
      ? "partner_admin"
      : isMember
        ? "member"
        : isGuest
          ? "guest"
          : "none";

  const cap = tier === "super_admin" ? null : tier === "none" ? 0 : CAPS[tier][feature];

  const { data: used = 0, refetch } = useQuery({
    enabled: !!user && tier !== "super_admin",
    queryKey: ["coach-quota", user?.id, feature],
    queryFn: async () => {
      const startOfDayUtc = new Date();
      startOfDayUtc.setUTCHours(0, 0, 0, 0);
      const { count } = await supabase
        .from("ai_usage_events")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user!.id)
        .eq("feature", feature)
        .gte("created_at", startOfDayUtc.toISOString());
      return count ?? 0;
    },
    staleTime: 30_000,
  });

  return {
    used,
    cap,
    remaining: cap === null ? Infinity : Math.max(0, cap - used),
    unlimited: cap === null,
    refetch,
  };
}